<script setup lang="ts">
  import { type PropType, computed } from 'vue';
  import type { PotaSpotType, SotaSpotType } from '@/types';
  import type { WatchlistRefKind } from '@/stores/WatchlistStore';
  import { formatMhz } from '@/utils/formatters';

  const props = defineProps({
    spot: {
      type: Object as PropType<PotaSpotType | SotaSpotType>,
      required: true
    },
    // The normalized watchlist entries that matched this spot (null when the
    // match was on the other field only).
    matchedCallsign: {
      type: String as PropType<string | null>,
      default: null
    },
    matchedRef: {
      type: String as PropType<string | null>,
      default: null
    },
    // Whether the matched reference is a park (POTA) or summit (SOTA); drives
    // the wording of the "Matched ..." line only.
    refLabel: {
      type: String as PropType<WatchlistRefKind>,
      default: 'park'
    }
  });

  const frequency = computed(() => formatMhz(props.spot.frequencyInMhz));
  // POTA spots carry parkName, SOTA spots carry summitName.
  const placeName = computed(() =>
    'parkName' in props.spot ? props.spot.parkName : props.spot.summitName
  );
</script>

<template>
  <div data-testid="toast">
    <div data-testid="toast__header" class="font-bold text-sm mb-1 flex">
      <span data-testid="toast__badge"
        class="inline-flex items-center text-[#2196f3] rounded-md bg-white px-1.5 py-0.5 text-xs font-bold uppercase">
        <i class="fa-solid fa-bell mr-1"></i> Watchlist
      </span>
      <span data-testid="toast__title" class="ml-2 text-base font-mono">
        {{ props.spot.activator }}
      </span>
    </div>
    <div data-testid="toast__body" class="text-sm">
      <div class="font-semibold capitalize">
        {{ props.spot.reference }}<span v-if="placeName"> &middot; {{ placeName }}</span>
      </div>
      <div class="font-medium font-mono mt-1">
        {{ frequency }} MHz &middot; {{ props.spot.mode }}
      </div>
      <div class="mt-1 text-xs text-white/80">
        Matched
        <span v-if="props.matchedCallsign" class="font-semibold">callsign {{ props.matchedCallsign }}</span>
        <span v-if="props.matchedCallsign && props.matchedRef"> and </span>
        <span v-if="props.matchedRef" class="font-semibold">{{ props.refLabel }} {{ props.matchedRef }}</span>
      </div>
    </div>
  </div>
</template>
